type DateValue = string | Date | null | undefined;

function toCalendarDateParts(value: DateValue): [number, number, number] | null {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return isNaN(value.getTime())
            ? null
            : [value.getFullYear(), value.getMonth() + 1, value.getDate()];
    }

    const text = value.trim();
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    const usMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
    const match = isoMatch || usMatch;

    if (!match) {
        return null;
    }

    const year = Number(match[isoMatch ? 1 : 3]);
    const month = Number(match[isoMatch ? 2 : 1]);
    const day = Number(match[isoMatch ? 3 : 2]);
    const parsed = new Date(year, month - 1, day);

    if (parsed.getFullYear() !== year || parsed.getMonth() + 1 !== month || parsed.getDate() !== day) {
        return null;
    }

    return [year, month, day];
}

function toCalendarDateNumber(value: DateValue): number | null {
    const parts = toCalendarDateParts(value);
    return parts ? (parts[0] * 10000) + (parts[1] * 100) + parts[2] : null;
}

export function toCustomFieldDateRuleParam(value: DateValue): string {
    const parts = toCalendarDateParts(value);
    if (!parts) {
        return '';
    }

    return `${parts[0]}-${String(parts[1]).padStart(2, '0')}-${String(parts[2]).padStart(2, '0')}`;
}

/** Local midnight for the calendar day (ISO prefix / local parts), not `new Date(iso)`. */
export function toCustomFieldCalendarDate(value: DateValue): Date | null {
    const parts = toCalendarDateParts(value);
    return parts ? new Date(parts[0], parts[1] - 1, parts[2]) : null;
}

export function formatCustomFieldDateRuleParam(value: DateValue): string {
    const parts = toCalendarDateParts(value);
    return parts ? `${parts[1]}/${parts[2]}/${parts[0]}` : '';
}

export function getCustomFieldDateRangeRule(
    minDate: DateValue,
    maxDate: DateValue
): [string, string] | null {
    if (!minDate && !maxDate) {
        return null;
    }

    return [
        toCustomFieldDateRuleParam(minDate),
        toCustomFieldDateRuleParam(maxDate)
    ];
}

export function isCustomFieldDateInRange(value: DateValue, minDate: DateValue, maxDate: DateValue): boolean {
    if (!value) {
        return true;
    }

    const date = toCalendarDateNumber(value);
    const min = toCalendarDateNumber(minDate);
    const max = toCalendarDateNumber(maxDate);

    if (date === null) {
        return false;
    }

    return (min === null || date >= min) && (max === null || date <= max);
}